import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PlusIcon,
  WalletIcon,
} from '@heroicons/react/24/outline'

import { walletAPI } from '../services/api'
import { WALLET_TYPE_OPTIONS, BILL_PERIOD_OPTIONS } from '../common/constants'
import { useI18n } from '../contexts/I18nContext'
import type { Wallet, WalletTypeFilter } from '../types/wallet'

export default function WalletsPage() {
  const { t } = useI18n()
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<WalletTypeFilter>('all')
  const [includeStopped, setIncludeStopped] = useState(false)
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Wallet | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null)

  const loadWallets = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await walletAPI.list({
        type: typeFilter === 'all' ? undefined : typeFilter,
        includeStopped,
      })
      setWallets(res.wallets)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to load wallets'))
    } finally {
      setLoading(false)
    }
  }, [includeStopped, t, typeFilter])

  useEffect(() => {
    void loadWallets()
  }, [loadWallets])

  useEffect(() => {
    if (menuOpenId === null) return

    function handleWindowClick(): void {
      setMenuOpenId(null)
    }

    window.addEventListener('click', handleWindowClick)
    return () => window.removeEventListener('click', handleWindowClick)
  }, [menuOpenId])

  async function handleToggle(wallet: Wallet): Promise<void> {
    setMenuOpenId(null)
    setTogglingId(wallet.id)
    try {
      await walletAPI.toggle(wallet.id)
      await loadWallets()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to toggle wallet'))
    } finally {
      setTogglingId(null)
    }
  }

  async function handleDelete(): Promise<void> {
    if (!deleteTarget) return
    try {
      await walletAPI.delete(deleteTarget.id)
      setDeleteTarget(null)
      await loadWallets()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to delete wallet'))
    }
  }

  const periodLabel = (period: string) =>
    t(BILL_PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? period)

  return (
    <div className="page">
      <div className="page__header">
        <h1>{t('Wallets')}</h1>
        <p>{t('Credit, cash, and standard money sources')}</p>
      </div>

      {/* Toolbar */}
      <div className="entity-toolbar">
        <div className="entity-filters">
          {WALLET_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`filter-chip${typeFilter === opt.value ? ' filter-chip--active' : ''}`}
              onClick={() => setTypeFilter(opt.value)}
            >
              {t(opt.label)}
            </button>
          ))}
          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={includeStopped}
              onChange={(e) => setIncludeStopped(e.target.checked)}
            />
            <span>{t('Show stopped')}</span>
          </label>
        </div>
        <Link to="/wallets/new" className="btn btn--primary">
          <PlusIcon />
          <span>{t('New Wallet')}</span>
        </Link>
      </div>

      {/* Error */}
      {error && <div className="alert alert--error">{error}</div>}

      {/* Loading */}
      {loading && (
        <div className="page__loading">
          <div className="loading-orb" />
        </div>
      )}

      {/* Empty */}
      {!loading && wallets.length === 0 && (
        <div className="empty-state">
          <WalletIcon />
          <p>{t('No wallets found')}</p>
          <Link to="/wallets/new" className="btn btn--primary">
            <PlusIcon />
            <span>{t('Add your first wallet')}</span>
          </Link>
        </div>
      )}

      {/* Wallet cards */}
      {!loading && wallets.length > 0 && (
        <div className="entity-grid">
          {wallets.map((w) => (
            <div
              key={w.id}
              className={`entity-card${w.stopped ? ' entity-card--stopped' : ''}${menuOpenId === w.id ? ' entity-card--menu-open' : ''}`}
            >
              <div className="entity-card__header">
                <div
                  className="entity-card__icon"
                  style={{ background: w.color || '#577590' }}
                >
                  {w.icon || w.name.charAt(0).toUpperCase()}
                </div>
                <div className="entity-card__title">
                  <span>{w.name}</span>
                  <div className="entity-card__badges">
                    {w.is_credit && <span className="badge badge--blue">{t('Credit')}</span>}
                    {w.is_cash && <span className="badge badge--green">{t('Cash')}</span>}
                    {w.stopped && <span className="badge badge--dim">{t('Stopped')}</span>}
                  </div>
                </div>
              </div>

              {w.description && (
                <p className="entity-card__desc">{w.description}</p>
              )}

              <div className="entity-card__meta">
                {w.bill_period && w.bill_period !== 'none' && (
                  <span>{periodLabel(w.bill_period)}</span>
                )}
                {w.bill_due_day > 0 && <span>{t('Due day')} {w.bill_due_day}</span>}
                {w.default_expense_type && (
                  <span>
                    {w.default_expense_type.icon} {w.default_expense_type.name}
                  </span>
                )}
              </div>

              <div className="entity-card__actions">
                <Link to={`/wallets/${w.id}`} className="icon-button" title={t('Edit')}>
                  <PencilIcon />
                </Link>
                <div
                  className="wallet-card-menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="icon-button"
                    onClick={() => setMenuOpenId((prev) => (prev === w.id ? null : w.id))}
                    title={t('More actions')}
                    aria-haspopup="menu"
                    aria-expanded={menuOpenId === w.id}
                  >
                    <EllipsisVerticalIcon />
                  </button>
                  {menuOpenId === w.id && (
                    <div className="wallet-card-menu__panel" role="menu">
                      <button
                        className="wallet-card-menu__item"
                        disabled={togglingId === w.id}
                        onClick={() => handleToggle(w)}
                        role="menuitem"
                      >
                        {w.stopped ? t('Resume wallet') : t('Stop wallet')}
                      </button>
                      <button
                        className="wallet-card-menu__item wallet-card-menu__item--danger"
                        onClick={() => {
                          setMenuOpenId(null)
                          setDeleteTarget(w)
                        }}
                        role="menuitem"
                      >
                        {t('Delete wallet')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <>
          <div className="modal-backdrop" onClick={() => setDeleteTarget(null)} />
          <div className="modal-wrap">
            <div className="modal modal--narrow">
              <div className="modal__header">
                <div className="modal__header-icon modal__header-icon--danger">
                  <ExclamationTriangleIcon />
                </div>
                <div>
                  <h3>{t('Delete Wallet')}</h3>
                  <p>
                    {t('Delete {name}? This cannot be undone.', { name: deleteTarget.name })}
                  </p>
                </div>
              </div>
              <div className="modal__actions">
                <button
                  className="btn btn--ghost"
                  onClick={() => setDeleteTarget(null)}
                >
                  {t('Cancel')}
                </button>
                <button className="danger-button" onClick={handleDelete}>
                  {t('Delete')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
